import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import {
	Button,
	Flex,
	FormControl,
	FormErrorMessage,
	FormLabel,
	Grid,
	HStack,
	Input,
	Select,
	Skeleton,
	Text,
	useColorModeValue,
	useToast
} from '@chakra-ui/react';
import { useCreatePost, useTemplates } from 'hooks/posts';

import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';

import { useAllTopics } from 'hooks/topics';
import UploadField from 'components/UploadField';
import TemplateFields from 'components/TemplateFields';

const schema = yup.object().shape({
	title: yup.string().required('O campo Título é obrigatório'),
	topic_id: yup.string().required('O campo Tópico é obrigatório'),
	template_id: yup.string().required('O campo Template é obrigatório')
});

const CreatePost = () => {
	const methods = useForm({
		mode: 'onBlur',
		reValidateMode: 'onBlur',
		resolver: yupResolver(schema)
	});
	const toast = useToast();
	const history = useHistory();

	const inputHover = useColorModeValue('appai.main', 'appai.main');
	const textColor = useColorModeValue('gray.700', 'white');

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
		watch
	} = methods;

	const [createPost, { loading }] = useCreatePost({
		onSuccess: (data) => {
			reset();
			toast({
				title: 'Post cadastrado com sucesso.',
				status: 'success',
				duration: 5000,
				isClosable: true
			});
			history.push(`/sistema/conteudos/posts/${data.createPost.id}`);
		},
		onError: () => {
			toast({
				title: 'Ocorreu um erro',
				status: 'error',
				duration: 5000,
				isClosable: true
			});
		}
	});

	const {
		data: { listTemplates: templates } = [],
		loading: loadingTemplates
	} = useTemplates();

	const { data: { listTopics: topics } = [], loading: loadingTopics } =
		useAllTopics();

	const onSubmit = (data) => {
		data.template_fields = JSON.stringify(data.template_fields);

		const variables = {
			input: {
				...data,
				status: 'draft',
				class: 'Post'
			}
		};

		createPost({ variables });
	};

	const [templateFields, setTemplateFields] = useState([]);
	const wTemplate = watch('template_id');

	useEffect(() => {
		if (!wTemplate) setTemplateFields([]);

		const fields = templates?.find(({ id }) => id === wTemplate)?.fields;

		if (fields) {
			const parsed = JSON.parse(JSON.parse(fields));
			const fieldsArr = [];

			Object.keys(parsed).forEach((key) => {
				const entry = {};
				entry[key] = parsed[key];
				fieldsArr.push(entry);
			});

			setTemplateFields(fieldsArr);
		}
	}, [wTemplate]);

	return (
		<Flex
			direction="column"
			minH="100vh"
			align="center"
			pt={{ sm: '120px', lg: '160px' }}
		>
			<Card w={{ md: '100%', lg: '50%' }}>
				<CardHeader mb="40px">
					<Flex direction="column">
						<Text
							color={textColor}
							fontSize="lg"
							fontWeight="bold"
							mb="3px"
						>
							Cadastrar Post
						</Text>
						<Text
							color="gray.400"
							fontWeight="normal"
							fontSize="sm"
						>
							Preencha os campos para realizar o cadastro
						</Text>
					</Flex>
				</CardHeader>
				<CardBody>
					<FormProvider {...methods}>
						<form
							style={{ width: '100%' }}
							onSubmit={handleSubmit(onSubmit)}
						>
							<Flex direction="column" w="100%">
								<Grid
									templateColumns={{
										sm: '1fr',
										md: 'repeat(2, 1fr)'
									}}
									gap="24px"
								>
									<FormControl isInvalid={errors.title}>
										<FormLabel
											color={textColor}
											fontWeight="bold"
											fontSize="xs"
										>
											Título
										</FormLabel>
										<Input
											{...register('title')}
											focusBorderColor={inputHover}
											borderRadius="8px"
											fontSize="md"
										/>
										{errors.title && (
											<FormErrorMessage>
												{errors.title.message}
											</FormErrorMessage>
										)}
									</FormControl>

									<FormControl isInvalid={errors.subtitle}>
										<FormLabel
											color={textColor}
											fontWeight="bold"
											fontSize="xs"
										>
											Subtítulo
										</FormLabel>
										<Input
											{...register('subtitle')}
											focusBorderColor={inputHover}
											borderRadius="8px"
											fontSize="md"
										/>
										{errors.subtitle && (
											<FormErrorMessage>
												{errors.subtitle.message}
											</FormErrorMessage>
										)}
									</FormControl>

									<FormControl isInvalid={errors.template_id}>
										<FormLabel
											color={textColor}
											fontWeight="bold"
											fontSize="xs"
										>
											Template
										</FormLabel>
										{loadingTemplates ? (
											<Skeleton height="40px" />
										) : (
											<Select
												{...register('template_id')}
												focusBorderColor={inputHover}
												borderRadius="8px"
												fontSize="md"
												placeholder="Selecione..."
											>
												{templates?.map((template) => (
													<option value={template.id}>
														{template.title}
													</option>
												))}
											</Select>
										)}
										{errors.template_id && (
											<FormErrorMessage>
												{errors.template_id.message}
											</FormErrorMessage>
										)}
									</FormControl>

									<FormControl isInvalid={errors.topic_id}>
										<FormLabel
											color={textColor}
											fontWeight="bold"
											fontSize="xs"
										>
											Tópico
										</FormLabel>
										{loadingTopics ? (
											<Skeleton height="40px" />
										) : (
											<Select
												{...register('topic_id')}
												focusBorderColor={inputHover}
												borderRadius="8px"
												fontSize="md"
												placeholder="Selecione..."
											>
												{topics?.map((topic) => (
													<option value={topic.id}>
														{topic.title}
													</option>
												))}
											</Select>
										)}
										{errors.topic_id && (
											<FormErrorMessage>
												{errors.topic_id.message}
											</FormErrorMessage>
										)}
									</FormControl>
									{wTemplate == 2 && (
										<FormControl
											isInvalid={errors.url_embed}
										>
											<FormLabel
												color={textColor}
												fontWeight="bold"
												fontSize="xs"
											>
												URL do Vídeo
											</FormLabel>
											<Input
												{...register('url_embed')}
												focusBorderColor={inputHover}
												borderRadius="8px"
												fontSize="md"
											/>
											{errors.url_embed && (
												<FormErrorMessage>
													{errors.url_embed.message}
												</FormErrorMessage>
											)}
										</FormControl>
									)}
								</Grid>

								<UploadField
									label="Imagem de capa"
									filePrefix="cover"
								/>

								<UploadField
									label="Arquivo para download"
									filePrefix="download"
								/>

								<TemplateFields fields={templateFields} />

								<Flex
									w="full"
									justifyContent="flex-end"
									mt="24px"
								>
									<HStack spacing={4}>
										<Button
											disabled={loading}
											variant="ghost"
											colorScheme="gray"
											alignSelf="flex-end"
											size="md"
											onClick={() => history.goBack()}
										>
											Cancelar
										</Button>
										<Button
											isLoading={loading}
											disabled={loading}
											colorScheme="purple"
											type="submit"
											alignSelf="flex-end"
											size="md"
										>
											Cadastrar
										</Button>
									</HStack>
								</Flex>
							</Flex>
						</form>
					</FormProvider>
				</CardBody>
			</Card>
		</Flex>
	);
};

export default CreatePost;
